import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown, Menu, X, ArrowUpRight } from "lucide-react";
import Logo from "./Logo";

const PRIMARY = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/team", label: "Our Team" },
  { to: "/legacy", label: "Legacy" },
  { to: "/projects", label: "Projects" },
  { to: "/blog", label: "Blog" },
];

const MORE = [
  { to: "/what-are-we-up-to", label: "What Are We Up To?" },
  { to: "/publications", label: "Publications" },
  { to: "/calendar", label: "Calendar" },
  { to: "/gallery", label: "Gallery" },
  { to: "/achievements", label: "Achievements" },
  { to: "/our-north-star", label: "Our North Star" },
  { to: "/rotary-club-of-bombay-central", label: "Rotary Club of Bombay Central" },
  { to: "/collaborators", label: "Collaborators & Sponsors" },
  { to: "/members", label: "Members" },
];

const ALL = [...PRIMARY, ...MORE, { to: "/contact", label: "Contact" }];

export default function Navbar({ introDone }) {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <>
      <motion.header
        className={`fixed inset-x-0 top-0 z-[90] transition-all duration-500 ${
          scrolled ? "glass border-b border-silver/10 py-3" : "bg-transparent py-5"
        }`}
        initial={{ y: -80, opacity: 0 }}
        animate={introDone ? { y: 0, opacity: 1 } : { y: -80, opacity: 0 }}
        transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1], delay: 0.2 }}
        data-testid="navbar"
      >
        <div className="mx-auto flex max-w-7xl items-center justify-between px-6">
          <Link to="/" className="shrink-0" data-testid="nav-logo-link">
            <Logo size={40} />
          </Link>

          <nav className="hidden items-center gap-7 lg:flex" data-testid="nav-primary">
            {PRIMARY.map((l) => (
              <NavLink key={l.to} to={l.to} end={l.to === "/"} className={({ isActive }) => `nav-link ${isActive ? "active" : ""}`} data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}>
                {l.label}
              </NavLink>
            ))}
            <div className="group relative">
              <button className="nav-link flex items-center gap-1.5" data-testid="nav-more-btn">
                More <ChevronDown size={13} className="transition-transform duration-300 group-hover:rotate-180" />
              </button>
              <div className="invisible absolute right-0 top-full w-64 translate-y-2 border border-silver/15 bg-navy/95 py-2 opacity-0 backdrop-blur-xl transition-all duration-300 group-hover:visible group-hover:translate-y-0 group-hover:opacity-100" data-testid="nav-more-menu">
                {MORE.map((l) => (
                  <NavLink key={l.to} to={l.to} className={({ isActive }) => `block px-5 py-3 text-xs uppercase tracking-[0.16em] transition-colors hover:bg-silver/5 hover:text-white ${isActive ? "text-white" : "text-silver-dim"}`} data-testid={`nav-more-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}>
                    {l.label}
                  </NavLink>
                ))}
              </div>
            </div>
            <Link to="/contact" className="btn-silver !px-5 !py-2.5" data-testid="nav-connect-cta">
              <span>Connect</span>
            </Link>
          </nav>

          <button className="text-silver lg:hidden" onClick={() => setOpen(true)} aria-label="Open menu" data-testid="mobile-menu-btn">
            <Menu size={26} />
          </button>
        </div>
      </motion.header>

      <AnimatePresence>
        {open && (
          <motion.div
            className="fixed inset-0 z-[95] flex flex-col bg-ink/97 px-6 py-6 backdrop-blur-2xl"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4 }}
            data-testid="mobile-menu"
          >
            <div className="flex items-center justify-between">
              <Logo size={40} />
              <button onClick={() => setOpen(false)} aria-label="Close menu" className="text-silver" data-testid="mobile-menu-close-btn">
                <X size={28} />
              </button>
            </div>
            <nav className="mt-12 flex flex-1 flex-col gap-1 overflow-y-auto" data-testid="mobile-menu-links">
              {ALL.map((l, i) => (
                <motion.div key={l.to} initial={{ opacity: 0, x: -24 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.06 * i, duration: 0.5 }}>
                  <NavLink
                    to={l.to}
                    end={l.to === "/"}
                    className={({ isActive }) =>
                      `flex items-center justify-between border-b border-silver/10 py-4 font-display text-2xl ${isActive ? "metallic" : "text-silver-dim"}`
                    }
                    data-testid={`mobile-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                  >
                    {l.label}
                    <ArrowUpRight size={18} className="text-ember" />
                  </NavLink>
                </motion.div>
              ))}
            </nav>
            <p className="eyebrow pb-2">Forged — Reclaiming the Fire</p>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
